// Shared US phone number helpers — formatting, validation, and E.164 conversion.
// Scope: US numbers only. International numbers are handled separately (e.g. the
// country-picker MFA flow in SettingsPage.js) and should NOT use these helpers.

// Strips a US phone value down to its significant 10 digits (drops a leading
// country-code "1" from an 11-digit number, keeps everything else as typed).
export const getUSPhoneDigits = (value) => {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
};

// Formats a raw input value into the standard US display format as the user
// types: (555) 123-4567 — optionally prefixed with +1 when a country code was
// entered (leading "+" or an 11-digit number starting with 1).
export const formatUSPhoneInput = (value) => {
  if (!value) return '';
  const rawDigits = value.replace(/\D/g, '');
  const hasCountryCode = value.trim().startsWith('+') ||
    (rawDigits.length === 11 && rawDigits.startsWith('1'));
  const digits = getUSPhoneDigits(value);
  const prefix = hasCountryCode ? '+1 ' : '';

  if (digits.length === 0) return prefix;
  if (digits.length < 4) return `${prefix}(${digits}`;
  if (digits.length < 7) return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Validates by digit count only — never rejects a number just because of how
// it's punctuated/spaced. Returns '' when valid, an error message otherwise.
export const getUSPhoneError = (value, { required = true } = {}) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return required ? 'Phone number is required' : '';

  const digits = getUSPhoneDigits(value);
  const rawDigitCount = trimmed.replace(/\D/g, '').length;

  if (rawDigitCount < 10 || rawDigitCount > 11) {
    return 'Please enter a valid 10-digit phone number';
  }
  if (/^(\d)\1{9}$/.test(digits)) {
    return 'Please enter a valid phone number';
  }
  return '';
};

// Converts a US phone value to E.164 (+1XXXXXXXXXX) for API submission.
export const toE164US = (value) => {
  if (!value) return '';
  const digits = getUSPhoneDigits(value);
  return digits.length === 10 ? `+1${digits}` : value;
};
